import React, { useCallback, useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { incomesApi } from '../../services/mybudgetApi';
import { Income, INCOME_TYPE_LABELS, IncomeType } from '../../types';
import { Card } from '../../components/shared/Card';
import { Loading } from '../../components/shared/Loading';
import { ActionButton } from '../../components/shared/ActionButton';
import { Input } from '../../components/shared/Input';
import { Select } from '../../components/shared/Select';
import { Modal, ModalActions } from '../../components/shared/Modal';
import { useToaster } from '../../components/shared/Toaster';
import { formatCurrency, formatMonthYear, MONTHS } from '../../constants';

export function IncomesPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [incomes, setIncomes] = useState<Income[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToaster();

  const [form, setForm] = useState({
    type: 'salary' as IncomeType,
    amount: '',
    description: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setIncomes(await incomesApi.list(year, month));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const total = incomes.reduce((s, i) => s + Number(i.amount), 0);

  const handleCreate = async () => {
    if (!form.amount) return;
    setSaving(true);
    try {
      await incomesApi.create({
        year,
        month,
        type: form.type,
        amount: Number(form.amount),
        description: form.description || undefined,
      });
      addToast({ type: 'success', message: 'Receita adicionada!' });
      setModalOpen(false);
      setForm({ type: 'salary', amount: '', description: '' });
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao salvar',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remover esta receita?')) return;
    try {
      await incomesApi.delete(id);
      addToast({ type: 'success', message: 'Receita removida' });
      load();
    } catch (err) {
      addToast({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao remover',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-100">Receitas</h2>
          <p className="text-sm text-gray-400">Salário e ganhos por mês</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select
            options={Array.from({ length: 5 }, (_, i) => ({
              value: now.getFullYear() - i,
              label: String(now.getFullYear() - i),
            }))}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
          <Select
            options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          />
          <ActionButton onClick={() => setModalOpen(true)}>
            <AddIcon fontSize="small" />
            Adicionar
          </ActionButton>
        </div>
      </div>

      <Card glow="income">
        <p className="text-sm text-gray-400">Total em {formatMonthYear(year, month)}</p>
        <p className="mt-1 text-3xl font-bold text-income">{formatCurrency(total)}</p>
      </Card>

      {loading ? (
        <Loading />
      ) : incomes.length === 0 ? (
        <Card>
          <p className="text-center text-gray-500">
            Nenhuma receita registrada neste mês. Adicione seu salário ou outros ganhos.
          </p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {incomes.map((income) => (
            <Card key={income.id} className="!p-0">
              <div className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="font-medium text-gray-100">
                    {INCOME_TYPE_LABELS[income.type]}
                  </p>
                  {income.description && (
                    <p className="text-sm text-gray-500">{income.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-lg font-semibold text-income">
                    {formatCurrency(Number(income.amount))}
                  </span>
                  <button
                    onClick={() => handleDelete(income.id)}
                    className="text-gray-500 hover:text-red-400"
                  >
                    <DeleteIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Nova receita"
        footer={
          <ModalActions
            onCancel={() => setModalOpen(false)}
            onConfirm={handleCreate}
            loading={saving}
          />
        }
      >
        <div className="space-y-4">
          <Select
            label="Tipo"
            options={Object.entries(INCOME_TYPE_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as IncomeType })}
          />
          <Input
            label="Valor (R$)"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            required
          />
          <Input
            label="Descrição (opcional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
}
